import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { apiGet, apiPath } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';
import { useAuthStore } from '@/lib/auth/store';

import type {
  GapMatrixResponse,
  GapReportResponse,
  GapType,
  IrrbbRegulatoryReport,
  LcrNsfrReportResponse,
  MatrixDimension,
  PresentationMode,
  StressReport,
} from './types';

const gapKey = (
  type: GapType,
  mode: PresentationMode,
  from: string,
  to: string,
  currency?: string,
  segmentationId?: string,
) => ['reporting', 'gap', type, mode, from, to, currency ?? 'all', segmentationId ?? 'none'] as const;
const gapMatrixKey = (
  date: string,
  dim: MatrixDimension,
  currency: string | undefined,
  segmentationId: string | undefined,
) => ['reporting', 'gap', 'matrix', date, segmentationId ?? dim, currency ?? 'all'] as const;
const lcrKey = (from: string, to: string) =>
  ['reporting', 'lcr', from, to] as const;
const irrbbKey = (date?: string) =>
  ['reporting', 'irrbb', date ?? 'latest'] as const;
const stressReportKey = (runId: string) =>
  ['reporting', 'stress', runId] as const;

/**
 * GET /api/v1/alm/reports/gap?type=…&mode=…&from=…&to=…[&currency=…][&segmentation=…]
 *
 * `mode` defaults to FLOW (tombée). PRINCIPAL (encours) returns the running
 * outstanding-balance view of the same calculation series — see spec §6.1.
 *
 * Optional filters (spec §6.1.3):
 *  - `currency`       ISO-4217 code to restrict to a single currency; omit for all.
 *  - `segmentationId` UUID of a named matrix segmentation to apply as row grouping.
 */
export function useGapReport(
  type: GapType,
  from: string,
  to: string,
  mode: PresentationMode = 'FLOW',
  currency?: string,
  segmentationId?: string,
  options?: Omit<
    UseQueryOptions<GapReportResponse, CubixApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: gapKey(type, mode, from, to, currency, segmentationId),
    queryFn: () => {
      const params = new URLSearchParams({ type, mode, from, to });
      if (currency)       params.set('currency',     currency);
      if (segmentationId) params.set('segmentation', segmentationId);
      const url = `${apiPath('alm', 'reports', 'gap')}?${params.toString()}`;
      return apiGet<GapReportResponse>(url);
    },
    enabled: Boolean(from && to),
    ...options,
  });
}

/**
 * GET /api/v1/alm/reports/gap/matrix
 *
 * Returns the Liquidity Gap pivot matrix for a single gold snapshot date.
 * Rows = chosen dimension (or segmentation label), columns = the 7 BAM buckets.
 *
 * Two modes:
 *  - With segmentationId: passes `segmentation=<uuid>` — backend overrides dim
 *    from the segmentation's dimension and maps values to custom labels.
 *  - Without segmentationId: passes `dim=<INSTRUMENT_TYPE|BS_CLASS|CURRENCY>`.
 *
 * @param date           ISO date string — leave undefined to use today's snapshot
 * @param dim            row pivot fallback (ignored when segmentationId is set)
 * @param currency       optional ISO-4217 filter; undefined = all currencies in MAD
 * @param segmentationId optional UUID of a named segmentation
 */
export function useGapMatrix(
  date: string,
  dim: MatrixDimension = 'INSTRUMENT_TYPE',
  currency?: string,
  segmentationId?: string,
  options?: Omit<
    UseQueryOptions<GapMatrixResponse, CubixApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: gapMatrixKey(date, dim, currency, segmentationId),
    queryFn: () => {
      const params = new URLSearchParams({ date });
      if (segmentationId) {
        params.set('segmentation', segmentationId);
      } else {
        params.set('dim', dim);
      }
      if (currency) params.set('currency', currency);
      const url = `${apiPath('alm', 'reports', 'gap', 'matrix')}?${params.toString()}`;
      return apiGet<GapMatrixResponse>(url);
    },
    enabled: Boolean(date),
    ...options,
  });
}

/**
 * GET /api/v1/alm/reports/lcr?from=…&to=…
 */
export function useLcrReport(
  from: string,
  to: string,
  options?: Omit<
    UseQueryOptions<LcrNsfrReportResponse, CubixApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: lcrKey(from, to),
    queryFn: () => {
      const url = `${apiPath('alm', 'reports', 'lcr')}?from=${from}&to=${to}`;
      return apiGet<LcrNsfrReportResponse>(url);
    },
    enabled: Boolean(from && to),
    ...options,
  });
}

/**
 * GET /api/v1/alm/reports/irrbb?date=YYYY-MM-DD  (date optional — returns latest)
 *
 * Assembles the BAM IRRBB regulatory report (EVE + NIM + Duration + KRD).
 * Always succeeds; check `metadata.partial` to warn the user that some
 * sections are missing.
 */
export function useIrrbbReport(
  date?: string,
  options?: Omit<
    UseQueryOptions<IrrbbRegulatoryReport, CubixApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: irrbbKey(date),
    queryFn: () => {
      const base = apiPath('alm', 'reports', 'irrbb');
      const url  = date ? `${base}?date=${date}` : base;
      return apiGet<IrrbbRegulatoryReport>(url);
    },
    ...options,
  });
}

/**
 * GET /api/v1/alm/reports/irrbb/xbrl?date=YYYY-MM-DD  (date optional — downloads latest)
 *
 * Streams the IRRBB XBRL instance document and triggers a browser download.
 * The server sets Content-Disposition: attachment; filename=IRRBB_{tenant}_{date}.xbrl.
 *
 * Uses native fetch (not axios) to obtain a Blob, then creates a temporary
 * object URL and clicks it — the cleanest cross-browser download approach
 * that works alongside the SPA router.
 *
 * @param date  ISO date string (yyyy-MM-dd); undefined → server picks latest EVE result
 * @throws Error when the server returns a non-2xx status (e.g. 400 when no EVE result)
 */
export async function downloadIrrbbXbrl(date?: string): Promise<void> {
  const base      = apiPath('alm', 'reports', 'irrbb', 'xbrl');
  const url       = date ? `${base}?date=${date}` : base;

  // Pull token and tenantId from the auth store (same source as axios interceptor)
  const { token, user } = useAuthStore.getState();
  const tenantId = user?.tenant_id ?? (import.meta.env.VITE_DEFAULT_TENANT as string | undefined);

  const headers: Record<string, string> = {
    Accept: 'application/xml',
  };
  if (token)    headers['Authorization'] = `Bearer ${token}`;
  if (tenantId) headers['X-Tenant-ID']   = tenantId;

  const response = await fetch(url, { method: 'GET', headers });

  if (!response.ok) {
    // Try to extract a server error message for a better UX error toast
    let detail = `HTTP ${response.status}`;
    try {
      const body = await response.json() as { error?: { message?: string }; message?: string };
      detail = body?.error?.message ?? body?.message ?? detail;
    } catch {
      // ignore parse failure
    }
    throw new Error(detail);
  }

  // Derive filename from Content-Disposition if present, else fall back
  const cd       = response.headers.get('Content-Disposition') ?? '';
  const match    = cd.match(/filename[^;=\n]*=["']?([^"'\n;]+)/);
  const filename = match ? match[1].trim() : `IRRBB_${date ?? 'latest'}.xbrl`;

  const blob    = await response.blob();
  const objUrl  = URL.createObjectURL(blob);
  const anchor  = document.createElement('a');
  anchor.href   = objUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Release the object URL on the next tick (after the download dialog fires)
  setTimeout(() => URL.revokeObjectURL(objUrl), 100);
}

/**
 * GET /api/v1/reporting/gap/export  (cx-reporting service, via gateway)
 *
 * Streams a ZIP with the FULL datamart export for a gold snapshot:
 *   - gap-matrix.csv           aggregated segment × bucket (every segment/bucket)
 *   - gap-schedule-detail.csv  raw gold.gap_cf_schedule rows behind each cell
 *
 * Unlike the old client-side CSV (which only dumped the on-screen matrix), this
 * is generated server-side from the datamart, so it includes the underlying
 * schedule detail regardless of what is currently rendered. Parameters mirror
 * the gap-matrix view so the export matches what's displayed.
 *
 * Uses native fetch (not axios) to obtain a Blob, then triggers a download —
 * same approach as downloadIrrbbXbrl.
 *
 * @throws Error when the server returns a non-2xx status.
 */
export async function downloadGapExport(args: {
  date?: string;
  dim?: MatrixDimension;
  segmentationId?: string;
  currency?: string;
}): Promise<void> {
  const params = new URLSearchParams();
  if (args.date)           params.set('date', args.date);
  if (args.segmentationId) params.set('segmentation', args.segmentationId);
  else if (args.dim)       params.set('dim', args.dim);
  if (args.currency)       params.set('currency', args.currency);

  const qs  = params.toString();
  const url = `${apiPath('reporting', 'gap', 'export')}${qs ? `?${qs}` : ''}`;

  // Pull token and tenantId from the auth store (same source as axios interceptor)
  const { token, user } = useAuthStore.getState();
  const tenantId = user?.tenant_id ?? (import.meta.env.VITE_DEFAULT_TENANT as string | undefined);

  const headers: Record<string, string> = { Accept: 'application/zip' };
  if (token)    headers['Authorization'] = `Bearer ${token}`;
  if (tenantId) headers['X-Tenant-ID']   = tenantId;

  const response = await fetch(url, { method: 'GET', headers });

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const body = await response.json() as { error?: { message?: string }; message?: string };
      detail = body?.error?.message ?? body?.message ?? detail;
    } catch {
      // ignore parse failure (e.g. empty body)
    }
    throw new Error(detail);
  }

  const cd       = response.headers.get('Content-Disposition') ?? '';
  const match    = cd.match(/filename[^;=\n]*=["']?([^"'\n;]+)/);
  const filename = match ? match[1].trim() : `gap-export-${args.date ?? 'latest'}.zip`;

  const blob   = await response.blob();
  const objUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href  = objUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(objUrl), 100);
}

/**
 * GET /api/v1/alm/reports/stress/{runId}
 *
 * Assembles a structured stress report for a given run:
 * shock parameters, base vs stressed LCR/NSFR/NIM/EVE, compliance verdict.
 */
export function useStressReport(
  runId: string | undefined,
  options?: Omit<
    UseQueryOptions<StressReport, CubixApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: stressReportKey(runId!),
    enabled: Boolean(runId),
    queryFn: () =>
      apiGet<StressReport>(apiPath('alm', 'reports', 'stress', runId!)),
    ...options,
  });
}
